import { createTheme } from "@material-ui/core/styles";

import { blue, grey } from "@material-ui/core/colors";

// A theme with custom primary and secondary color.
export const theme = createTheme({
  palette: {
    type: "light",
    primary: {
      main: blue[600],
    },
    secondary: {
      main: blue[800], // blueGrey[500],
    },
    text: {
      disabled: grey[600],
    },
    action: {
      hover: "rgba(0, 0, 0, 0.1)",
    },
    background: {
      default: grey[200],
    },
  },
  typography: {
    useNextVariants: true,
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 800,
  },
  overrides: {
    // managerApp:Menu:Subscriptions: Prevent elements with href="#" from appearing as active
    RaMenuItemLink: {
      root: {
        '&[href="#"]': {
          color: "inherit", // Use inherited color instead of primary color
        },
      },
    },

    // Table header overrides to prevent theme switching issues
    MuiTableCell: {
      head: {
        backgroundColor: "#FFFFFF !important", // force default white header background
        borderBottom: "1px solid #929292 !important", // Datagrid header divider
      },
    },
  },
});

export const darkTheme = createTheme({
  palette: {
    type: "dark",
    primary: {
      main: blue[600], // #1E88E5
    },
    secondary: {
      main: blue[300], // #ff5722
    },
    text: {
      disabled: grey[500],
    },
    action: {
      hover: "rgba(0, 0, 0, 0.1)",
    },
  },
  typography: {
    useNextVariants: true,
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 800,
  },
  overrides: {
    // managerApp:Menu:Subscriptions: Prevent elements with href="#" from appearing as active
    RaMenuItemLink: {
      root: {
        '&[href="#"]': {
          color: "inherit", // Use inherited color instead of primary color
        },
      },
    },

    // Table header overrides to prevent theme switching issues
    MuiTableCell: {
      head: {
        backgroundColor: "#424242 !important", // force default white header background
        borderBottom: "1px solid #929292 !important", // Datagrid header divider
      },
    },
  },
});

// High Contrast Light Theme - GitHub style with maximum contrast
export const highContrastLightTheme = createTheme({
  palette: {
    type: "light",
    primary: {
      main: "#000000", // Pure black for maximum contrast
    },
    secondary: {
      main: "#212121", // Very dark gray
    },
    text: {
      primary: "#000000", // Pure black for maximum contrast
      secondary: "#000000", // Pure black for maximum contrast
      disabled: "#000000", // Pure black for maximum contrast
    },
    action: {
      hover: "rgba(0, 0, 0, 0.1)", // Black hover
    },
    background: {
      default: "#FFFFFF", // Pure white
      paper: "#FFFFFF", // Pure white
    },
    error: {
      main: "#D32F2F", // Material Red 700 - High contrast red
    },
    success: {
      main: "#388E3C", // Material Green 700 - High contrast green
    },
    warning: {
      main: "#F57C00", // Material Orange 600 - High contrast orange
    },
    info: {
      main: "#1976D2", // Material Blue 700 - High contrast blue
    },
    divider: "#000000", // Black dividers
  },
  typography: {
    useNextVariants: true,
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 800,
  },
  overrides: {
    // Allow environment colors in AppBars for high contrast themes
    MuiAppBar: {
      colorPrimary: {
        backgroundColor: blue[600],
        color: "#ffffff !important", // Force white text/icons for proper contrast
      },
    },

    // GitHub-style components (no AppBar conflicts)
    MuiDialog: {
      paper: {
        border: "3px solid #000000", // Black border instead of shadow
        boxShadow: "none", // No shadow for battery saving
      },
    },

    MuiButton: {
      contained: {
        backgroundColor: "#000000", // Black
        color: "#FFFFFF", // White
        border: "2px solid #000000",
        "&:hover": {
          backgroundColor: "#212121", // Dark gray on hover
        },
      },
      outlined: {
        border: "2px solid #000000",
        color: "#000000",
        backgroundColor: "transparent",
        "&:hover": {
          backgroundColor: "#000000",
          color: "#FFFFFF",
        },
      },
      text: {
        color: "#000000",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.1)",
        },
      },
    },

    MuiCard: {
      root: {
        border: "3px solid #000000",
        backgroundColor: "#FFFFFF", // White solid background
        boxShadow: "0 6px 12px rgba(0, 0, 0, 0.4)",
      },
    },

    MuiPaper: {
      outlined: {
        border: "3px solid #000000",
      },
    },

    // Form elements - maximum contrast
    MuiTextField: {
      root: {
        "& .MuiInputLabel-root": {
          color: "#000000 !important",
        },
        "& .MuiOutlinedInput-root": {
          color: "#000000 !important",
          border: "2px solid #000000",
          "& fieldset": {
            borderColor: "#000000",
          },
          "&:hover fieldset": {
            borderColor: "#000000",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#000000",
          },
        },
      },
    },

    // Table components - maximum contrast
    MuiTableHead: {
      root: {
        backgroundColor: "#FFFFFF !important",
        color: "#000000 !important",
        borderBottom: "2px solid #000000 !important",
      },
    },

    MuiTableCell: {
      head: {
        backgroundColor: "#FFFFFF !important",
        color: "#000000 !important",
        borderBottom: "2px solid #000000 !important",
        borderRight: "1px solid #000000 !important", // Black vertical dividers for high contrast
        fontWeight: "bold !important",
      },
      body: {
        backgroundColor: "#FFFFFF !important",
        color: "#000000 !important",
        borderBottom: "1px solid #000000 !important",
        borderRight: "1px solid #000000 !important", // Black vertical dividers for high contrast
      },
    },

    MuiTableRow: {
      root: {
        backgroundColor: "#FFFFFF !important",
        transition: "background-color 0.2s ease-in-out", // Add transition
        "&:hover": {
          backgroundColor: "#F5F5F5 !important",
          border: "2px solid #000000 !important",
        },
        // Force re-application of styles
        "&:nth-child(even)": {
          backgroundColor: "#FFFFFF !important",
        },
        "&:nth-child(odd)": {
          backgroundColor: "#FFFFFF !important",
        },
      },
    },

    // React-admin specific table components
    MuiDatagrid: {
      root: {
        backgroundColor: "#FFFFFF !important",
      },
    },

    // Clean menu items without borders
    MuiListItem: {
      root: {
        color: "#000000 !important",
        "&:hover": {
          backgroundColor: "#F5F5F5 !important",
          border: "2px solid #000000 !important",
        },
      },
    },

    MuiMenu: {
      paper: {
        backgroundColor: "#FFFFFF !important",
        border: "1px solid #000000 !important",
      },
    },

    MuiMenuItem: {
      root: {
        backgroundColor: "transparent !important",
        color: "#000000 !important",
        borderBottom: "none !important", // Remove borders from menu items
        "&:hover": {
          backgroundColor: "#F5F5F5 !important",
          color: "#000000 !important",
          border: "2px solid #000000 !important",
        },
      },
    },

    // React-admin menu links
    RaMenuItemLink: {
      root: {
        '&[href="#"]': {
          color: "inherit !important",
        },
        borderBottom: "none !important",
        color: "#000000 !important",
        "&:hover": {
          backgroundColor: "#F5F5F5 !important",
          color: "#000000 !important",
          border: "2px solid #000000 !important",
        },
      },
    },
  },
});

// High Contrast Dark Theme - GitHub style with maximum contrast
export const highContrastDarkTheme = createTheme({
  palette: {
    type: "dark",
    primary: {
      main: "#ffffff", // Pure white for maximum contrast
    },
    secondary: {
      main: "#ffffff", // Pure white for secondary elements
    },
    text: {
      primary: "#ffffff", // Pure white text
      secondary: "#ffffff", // Pure white for secondary text
      disabled: "#ffffff", // Pure white even for disabled
    },
    action: {
      hover: "rgba(255, 255, 255, 0.2)", // Stronger white hover
    },
    background: {
      default: "#0d1117", // GitHub main background
      paper: "#161b22", // GitHub secondary background
    },
    error: {
      main: "#ff0000", // Pure red for maximum contrast
    },
    success: {
      main: "#00ff00", // Pure green for maximum contrast
    },
    warning: {
      main: "#ffff00", // Pure yellow for maximum contrast
    },
    info: {
      main: "#00ffff", // Pure cyan for maximum contrast
    },
    divider: "#ffffff", // Pure white dividers
  },
  typography: {
    useNextVariants: true,
    fontWeightLight: 400,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 800,
  },
  overrides: {
    // Allow environment colors in AppBars for high contrast themes
    MuiAppBar: {
      colorPrimary: {
        backgroundColor: blue[600],
        color: "#ffffff !important", // Force white text/icons for proper contrast
      },
    },

    // GitHub-style components (no AppBar conflicts)
    MuiDialog: {
      paper: {
        border: "3px solid #FFFFFF", // White border instead of shadow
        boxShadow: "none", // No shadow for battery saving
        backgroundColor: "#161b22",
      },
    },

    // Maximum contrast elements - pure black and white
    MuiButton: {
      contained: {
        backgroundColor: "#ffffff", // Pure white
        color: "#000000", // Pure black text
        border: "2px solid #ffffff",
        fontWeight: "bold",
        "&:hover": {
          backgroundColor: "#000000", // Pure black on hover
          color: "#ffffff", // Pure white text on hover
        },
      },
      outlined: {
        border: "2px solid #ffffff",
        color: "#ffffff",
        backgroundColor: "transparent",
        "&:hover": {
          backgroundColor: "#ffffff",
          color: "#000000",
        },
      },
    },

    // Cards and papers
    MuiCard: {
      root: {
        border: "3px solid #ffffff",
        backgroundColor: "#161b22",
        boxShadow: "0 6px 12px rgba(255, 255, 255, 0.3)",
      },
    },

    MuiPaper: {
      outlined: {
        border: "3px solid #ffffff",
        backgroundColor: "#161b22",
      },
    },

    // Form elements
    MuiTextField: {
      root: {
        "& .MuiInputLabel-root": {
          color: "#ffffff !important",
        },
        "& .MuiOutlinedInput-root": {
          color: "#ffffff !important",
          border: "2px solid #ffffff",
          "& fieldset": {
            borderColor: "#ffffff",
          },
          "&:hover fieldset": {
            borderColor: "#ffffff",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#ffffff",
          },
        },
      },
    },

    // Typography
    MuiTypography: {
      root: {
        color: "#ffffff !important",
      },
    },

    // Table components - GitHub style
    MuiTableHead: {
      root: {
        backgroundColor: "#161b22 !important", // GitHub secondary background
        color: "#ffffff !important",
      },
    },

    MuiTableHeader: {
      root: {
        backgroundColor: "#161b22 !important", // GitHub secondary background
        color: "#ffffff !important",
      },
    },

    MuiTableCell: {
      head: {
        backgroundColor: "#161b22 !important", // GitHub secondary background
        color: "#ffffff !important",
        borderBottom: "2px solid #ffffff !important", // Increased thickness
        borderRight: "1px solid #ffffff !important", // More prominent vertical dividers
        fontWeight: "bold !important",
      },
      body: {
        backgroundColor: "#0d1117 !important", // GitHub main background
        color: "#ffffff !important",
        borderBottom: "2px solid #ffffff !important", // More prominent horizontal dividers
        borderRight: "1px solid #ffffff !important", // More prominent vertical dividers
      },
    },

    MuiTableRow: {
      root: {
        backgroundColor: "#0d1117 !important",
        transition: "background-color 0.2s ease-in-out", // Add transition
        "&:hover": {
          backgroundColor: "#404040 !important",
          border: "2px solid #ffffff !important",
        },
        // Force re-application of styles
        "&:nth-child(even)": {
          backgroundColor: "#0d1117 !important",
        },
        "&:nth-child(odd)": {
          backgroundColor: "#0d1117 !important",
        },
      },
    },

    // React-admin specific table components
    MuiDatagrid: {
      root: {
        backgroundColor: "#0d1117 !important", // GitHub main background
      },
    },

    // List items - pure white (but no borders for menus)
    MuiListItem: {
      root: {
        color: "#ffffff !important",
        "&:hover": {
          backgroundColor: "#404040 !important", // Contrasting gray for highlight
          color: "#ffffff !important", // Pure white for maximum contrast
          border: "2px solid #ffffff !important", // White border for highlight
        },
      },
    },

    MuiListItemText: {
      primary: {
        color: "#ffffff !important",
        fontWeight: "bold",
      },
      secondary: {
        color: "#ffffff !important",
      },
    },

    MuiListItemIcon: {
      root: {
        color: "#ffffff !important",
      },
    },

    // React-admin UserMenu components - remove borders
    MuiMenu: {
      paper: {
        backgroundColor: "#161b22 !important", // GitHub secondary background
        border: "1px solid #ffffff !important",
      },
    },

    MuiMenuItem: {
      root: {
        backgroundColor: "transparent !important",
        color: "#ffffff !important",
        borderBottom: "none !important", // Remove borders from menu items
        "&:hover": {
          backgroundColor: "#404040 !important", // Contrasting gray for highlight
          color: "#ffffff !important", // Pure white for maximum contrast
          border: "2px solid #ffffff !important", // White border for highlight
        },
      },
    },

    // React-admin menu links
    RaMenuItemLink: {
      root: {
        '&[href="#"]': {
          color: "inherit !important",
        },
        borderBottom: "none !important",
        color: "#ffffff !important",
        "&:hover": {
          backgroundColor: "#404040 !important",
          color: "#ffffff !important",
          border: "2px solid #ffffff !important",
        },
      },
    },

    // Tabs - pure black and white
    MuiTab: {
      root: {
        color: "#ffffff !important",
        backgroundColor: "transparent",
        border: "1px solid #ffffff",
        "&.Mui-selected": {
          backgroundColor: "#ffffff !important",
          color: "#000000 !important",
        },
      },
    },

    MuiTabs: {
      indicator: {
        backgroundColor: "#ffffff",
      },
    },
  },
});

